import { useState, useEffect, useCallback } from 'react';

const COOKIE_NAME = 'cybermonit_dashboard_settings';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

// Individual metrics visibility
export interface MetricsSettings {
    ransomwareVictims: boolean;
    dataLeaks: boolean;
    activeGroup: boolean;
    mostAttackedCountry: boolean;
    criticalCVE: boolean;
    highCVE: boolean;
    mediumCVE: boolean;
    lowCVE: boolean;
}

// Ransomware filter settings
export interface RansomwareFilters {
    countries: string[]; // Empty = all
    groups: string[];    // Empty = all
}

// Card size configuration
export interface CardSize {
    width: 1 | 2 | 3;  // Columns span
    height: number;    // Row span (1=5 items, 2=10 items, 3=15 items)
}

// Card sizes for each draggable card
export interface CardSizes {
    mapWidget: CardSize;
    vulnerabilitiesCard: CardSize;
    highVulnerabilitiesCard: CardSize;   // NEW
    mediumVulnerabilitiesCard: CardSize; // NEW
    lowVulnerabilitiesCard: CardSize;    // NEW
    newsCard: CardSize;
    ransomwareCard: CardSize;
    leaksCard: CardSize;
    eolCard: CardSize;
    releasesCard: CardSize;
}

// Main settings interface
export interface DashboardSettings {
    // Card visibility
    metricsRow: boolean;
    cveMetrics: boolean;
    mapWidget: boolean;
    newsCard: boolean;
    ransomwareCard: boolean;
    leaksCard: boolean;
    vulnerabilitiesCard: boolean;
    highVulnerabilitiesCard: boolean;   // NEW
    mediumVulnerabilitiesCard: boolean; // NEW
    lowVulnerabilitiesCard: boolean;    // NEW
    eolCard: boolean;
    releasesCard: boolean;

    // Layout Order
    layoutOrder: string[];

    // Individual metrics
    metrics: MetricsSettings;

    // Filters
    ransomwareFilters: RansomwareFilters;
    newsSources: string[];      // Empty = all
    releaseProducts: string[];  // Empty = all
    eolProducts: string[];      // Empty = all

    // Card sizes (width & height)
    cardSizes: CardSizes;
}

const DEFAULT_METRICS: MetricsSettings = {
    ransomwareVictims: true,
    dataLeaks: true,
    activeGroup: true,
    mostAttackedCountry: true,
    criticalCVE: true,
    highCVE: true,
    mediumCVE: true,
    lowCVE: true,
};

// Default sizes as requested: 
// Map 2x1, Ransomware 1x1
// Leaks 1x1, Critical Vulns 2x1
// News 1x1, EOL 1x1, Releases 1x1
const DEFAULT_CARD_SIZES: CardSizes = {
    mapWidget: { width: 2, height: 1 },
    ransomwareCard: { width: 1, height: 1 },
    leaksCard: { width: 1, height: 1 },
    vulnerabilitiesCard: { width: 2, height: 1 },
    newsCard: { width: 1, height: 1 },
    eolCard: { width: 1, height: 1 },
    releasesCard: { width: 1, height: 1 },
    highVulnerabilitiesCard: { width: 1, height: 1 },   // Default small
    mediumVulnerabilitiesCard: { width: 1, height: 1 }, // Default small
    lowVulnerabilitiesCard: { width: 1, height: 1 },    // Default small
};

// Default order: Map, Ransomware, Leaks, Vulns (Critical), News, EOL, Releases
const DEFAULT_LAYOUT_ORDER = [
    'mapWidget',
    'ransomwareCard',
    'leaksCard',
    'vulnerabilitiesCard',
    'newsCard',
    'eolCard',
    'releasesCard',
    'highVulnerabilitiesCard',
    'mediumVulnerabilitiesCard',
    'lowVulnerabilitiesCard'
];

const DEFAULT_SETTINGS: DashboardSettings = {
    metricsRow: true,
    cveMetrics: true,
    mapWidget: true,
    ransomwareCard: true,
    leaksCard: true,
    vulnerabilitiesCard: true,
    newsCard: true,
    eolCard: true,
    releasesCard: true,
    // New cards hidden by default
    highVulnerabilitiesCard: false,
    mediumVulnerabilitiesCard: false,
    lowVulnerabilitiesCard: false,
    layoutOrder: DEFAULT_LAYOUT_ORDER,
    metrics: DEFAULT_METRICS,
    ransomwareFilters: { countries: [], groups: [] },
    newsSources: [],
    releaseProducts: [],
    eolProducts: [],
    cardSizes: DEFAULT_CARD_SIZES,
};

// Helper function to compute item count from card height
// 1 = 5, 2 = 10, 3 = 15
export function getItemCountFromHeight(height: number): number {
    const clampedHeight = Math.min(3, Math.max(1, height));
    return clampedHeight * 5;
}

function getCookie(name: string): string | null {
    if (typeof document === 'undefined') return null;
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
        return parts.pop()?.split(';').shift() || null;
    }
    return null;
}

function setCookie(name: string, value: string, maxAge: number) {
    if (typeof document === 'undefined') return;
    document.cookie = `${name}=${value}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

function loadSettings(): DashboardSettings {
    try {
        const cookie = getCookie(COOKIE_NAME);
        if (cookie) {
            const parsed = JSON.parse(decodeURIComponent(cookie));
            // Deep merge with defaults to handle new fields
            // Deep merge with defaults to handle new fields
            const mergedSettings: DashboardSettings = {
                ...DEFAULT_SETTINGS,
                ...parsed,
                metrics: { ...DEFAULT_METRICS, ...parsed.metrics },
                ransomwareFilters: {
                    countries: parsed.ransomwareFilters?.countries || [],
                    groups: parsed.ransomwareFilters?.groups || []
                },
                newsSources: parsed.newsSources || [],
                releaseProducts: parsed.releaseProducts || [],
                eolProducts: parsed.eolProducts || [],
                cardSizes: { ...DEFAULT_CARD_SIZES, ...parsed.cardSizes },
                layoutOrder: parsed.layoutOrder || DEFAULT_LAYOUT_ORDER,
                // Ensure new fields exist if loading old cookie
                highVulnerabilitiesCard: parsed.highVulnerabilitiesCard ?? DEFAULT_SETTINGS.highVulnerabilitiesCard,
                mediumVulnerabilitiesCard: parsed.mediumVulnerabilitiesCard ?? DEFAULT_SETTINGS.mediumVulnerabilitiesCard,
                lowVulnerabilitiesCard: parsed.lowVulnerabilitiesCard ?? DEFAULT_SETTINGS.lowVulnerabilitiesCard,
            };

            // Post-process to ensure all enabled cards are in layoutOrder (fixes migration issue)
            const enabledCards = [
                'mapWidget', 'ransomwareCard', 'leaksCard', 'vulnerabilitiesCard',
                'newsCard', 'eolCard', 'releasesCard',
                'highVulnerabilitiesCard', 'mediumVulnerabilitiesCard', 'lowVulnerabilitiesCard'
            ].filter(id => mergedSettings[id as keyof DashboardSettings] === true);

            enabledCards.forEach(id => {
                if (!mergedSettings.layoutOrder.includes(id)) {
                    mergedSettings.layoutOrder.push(id);
                }
            });

            return mergedSettings;
        }
    } catch (e) {
        console.error('Failed to parse dashboard settings cookie:', e);
    }
    return DEFAULT_SETTINGS;
}

function saveSettings(settings: DashboardSettings) {
    try {
        const value = encodeURIComponent(JSON.stringify(settings));
        setCookie(COOKIE_NAME, value, COOKIE_MAX_AGE);
    } catch (e) {
        console.error('Failed to save dashboard settings cookie:', e);
    }
}

export function useDashboardSettings() {
    const [settings, setSettings] = useState<DashboardSettings>(DEFAULT_SETTINGS);
    const [isLoaded, setIsLoaded] = useState(false);

    // Load settings from cookie on mount
    useEffect(() => {
        const loaded = loadSettings();
        setSettings(loaded);
        setIsLoaded(true);
    }, []);

    // Save settings to cookie whenever they change (after initial load)
    useEffect(() => {
        if (isLoaded) {
            saveSettings(settings);
        }
    }, [settings, isLoaded]);

    const toggleCard = useCallback((cardId: keyof Omit<DashboardSettings, 'metrics' | 'ransomwareFilters' | 'newsSources' | 'releaseProducts' | 'cardSizes' | 'layoutOrder'>) => {
        setSettings(prev => {
            const newState = !prev[cardId as keyof DashboardSettings];
            let newLayoutOrder = [...prev.layoutOrder];

            // If enabling and not in layout, add it
            if (newState && !newLayoutOrder.includes(cardId)) {
                newLayoutOrder.push(cardId);
            }
            // Optional: remove if disabling? User might want to keep position.
            // Let's keep it simple: just ensure it's in layout when enabled.

            return {
                ...prev,
                [cardId]: newState,
                layoutOrder: newLayoutOrder
            };
        });
    }, []);

    const toggleMetric = useCallback((metricId: keyof MetricsSettings) => {
        setSettings(prev => ({
            ...prev,
            metrics: {
                ...prev.metrics,
                [metricId]: !prev.metrics[metricId],
            },
        }));
    }, []);

    const setRansomwareFilters = useCallback((filters: RansomwareFilters) => {
        setSettings(prev => ({
            ...prev,
            ransomwareFilters: filters,
        }));
    }, []);

    const setNewsSources = useCallback((sources: string[]) => {
        setSettings(prev => ({
            ...prev,
            newsSources: sources,
        }));
    }, []);

    const setReleaseProducts = useCallback((products: string[]) => {
        setSettings(prev => ({
            ...prev,
            releaseProducts: products,
        }));
    }, []);

    const setEolProducts = useCallback((products: string[]) => {
        setSettings(prev => ({
            ...prev,
            eolProducts: products,
        }));
    }, []);

    const setLayoutOrder = useCallback((order: string[]) => {
        setSettings(prev => ({
            ...prev,
            layoutOrder: order
        }));
    }, []);

    const setCardSize = useCallback((cardId: keyof CardSizes, size: Partial<CardSize>) => {
        setSettings(prev => ({
            ...prev,
            cardSizes: {
                ...prev.cardSizes,
                [cardId]: {
                    ...prev.cardSizes[cardId],
                    ...size,
                }
            }
        }));
    }, []);

    const resetToDefaults = useCallback(() => {
        setSettings(DEFAULT_SETTINGS);
    }, []);

    const updateSettings = useCallback((newSettings: Partial<DashboardSettings>) => {
        setSettings(prev => ({ ...prev, ...newSettings }));
    }, []);

    return {
        settings,
        isLoaded,
        toggleCard,
        toggleMetric,
        setRansomwareFilters,
        setNewsSources,
        setReleaseProducts,
        setEolProducts,
        setLayoutOrder,
        setCardSize,
        resetToDefaults,
        updateSettings,
        DEFAULT_SETTINGS,
    };
}
